"use client";

import React from "react";
import Spinner from "../../spinner";
import { IButtonProps } from "./types";
import Typography from "../../typography";

/**
 * Component for a button with optional color. A primary button has a colored background.
 * @param interface
 * @returns a button.
 */
const Button = React.forwardRef<HTMLButtonElement | null, IButtonProps>(
  (
    {
      text,
      onClick,
      tooltip,
      primary = false,
      disabled = false,
      wide = false,
      spaced = false,
      loading = 0,
      redoutline = false,
      inversePrimary = false,
      children,
    },
    ref,
  ) => {
    const colorClass = inversePrimary ? "text-inverseMenuText" : primary ? "text-white" : "text-blueDark";
    const backgroundColorClass = inversePrimary
      ? "bg-inverseMenuBackground"
      : primary
        ? disabled
          ? "bg-greyShadow"
          : "bg-primary"
        : "bg-white";
    const borderColor = redoutline ? "border-red" : primary ? (disabled ? "border-grey" : "border-primary") : "border-primary";
    const hoverBorderColor = primary ? "hover:border-blueHover" : "hover:border-primary";
    const hoverBackgroundColor = primary ? (disabled ? "hover:bg-shadow" : "hover:bg-primary") : "hover:bg-blueHover";
    const hoverTextColor = primary ? "hover:text-white" : "hover:text-primary";

    const disabledBackgroundColor = "disabled:bg-greyShadow";
    const disabledTextColor = loading ? "disabled:text-greyDark" : "disabled:text-greyBackground";
    const disabledBorderColor = loading ? "disbled:border-greyShadow" : "disabled:border-greyShadow";

    const minWidth = wide ? "min-w-40" : "min-w-28";

    const spacedClass = spaced ? "px-9 py-2" : "px-4 py-2";

    const classCombined = `${colorClass} ${backgroundColorClass} ${borderColor} ${hoverBorderColor} ${hoverBackgroundColor} ${hoverTextColor} ${disabledBackgroundColor} ${disabledBorderColor} ${disabledTextColor} ${minWidth} ${spacedClass}`;

    return (
      <button
        title={tooltip}
        onClick={onClick}
        disabled={disabled || loading > 0}
        ref={ref}
        type="button"
        className={`${classCombined} rounded-full border transition-colors ease-in-out duration-200 ${
          !disabled ? "hover:scale-105" : ""
        } flex space-x-2 items-center justify-center`}
      >
        {loading ? <Spinner size="small" /> : null}
        {text && <Typography variant="DescriptionBold">{text}</Typography>}
        {children || null}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
